/**
 * Dashboard API contract types (E3-1).
 *
 * These mirror the three read-only endpoints the MAC Command Center renders
 * against. The frontend builds against this contract today through the MSW mock
 * layer (`src/mocks/`); Matt's Django/DRF backend implements the same shapes
 * later under `backend/apps/dashboard_api/`. Keeping the wire shapes in one
 * place lets the client functions, the mock handlers, and the metric registry
 * all share a single source of truth.
 */
import type { EngagementBand } from "./insights.types";
import type { LicenseState } from "./licensing.types";

/**
 * A membership tier name. Drives both summary breakdowns and member rows.
 *
 * #250: the real CE21 roster carries ~19 distinct tier groups (Established
 * Doctor, Club Member CA, Retired, New Doctor 2nd Year, …) — NOT a fixed
 * four-name set. The tier name is therefore an arbitrary backend-supplied
 * string; the union below documents the well-known MAC tiers for editor
 * autocomplete, but `string` keeps every real tier flowing through the summary,
 * the member rows, and the `/members?tier=` drill-down without being dropped.
 */
export type KnownMemberTier =
  | "Student"
  | "Century Club"
  | "Presidents Club"
  | "General";
export type MemberTier = KnownMemberTier | (string & {});

/** MAC districts are numbered 1–8. */
export type District = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

/**
 * Lifecycle status of a member record — the CANONICAL derived standing
 * (`members.MemberAudienceProfile.standing`), which is the same source the
 * Membership desk (`src/api/desk.ts`'s `DeskStanding`) reads.
 *
 * Issue #592: this used to be a projection of CE21's raw `Member.status` string
 * with an "if in doubt, active" default. CE21's `Customer` payload carries no
 * status field, so that column is blank for every mirrored person and the
 * default fired for all of them — the Status column read "Active" for the whole
 * roster while filtering by Active returned nobody. Two consequences here:
 *
 *  - `"pending"` is gone. It was only ever reachable from that raw-status map.
 *  - `"suspended"` (issue #603, membership lost), `"non_member"` and
 *    `"unknown"` are new. `"unknown"` means we have no standing on record for
 *    this person (on the live roster: the merged-away duplicates) — the one
 *    answer the old default could never give.
 */
export type MemberStatus =
  | "active"
  | "lapsed"
  | "suspended"
  | "non_member"
  | "deceased"
  | "unknown";

/**
 * Engagement / lapse-risk band (E14-1). Derived from a 0–100 engagement score:
 * `low` = engaged/likely-to-renew, `high` = needs re-engagement.
 */
export type RiskBand = "low" | "medium" | "high";

/** `GET /api/dashboard/summary` — one breakdown bucket: tier → count. */
export interface TierCount {
  tier: MemberTier;
  count: number;
}

/** `GET /api/dashboard/summary` — one breakdown bucket: district → count. */
export interface DistrictCount {
  district: District;
  count: number;
}

/**
 * `GET /api/dashboard/summary` — license-expiry buckets. `window` is a
 * human label for the expiry horizon (e.g. "30 days", "90 days").
 */
export interface LicenseExpiringCount {
  window: string;
  count: number;
}

/** `GET /api/dashboard/summary` — convention registration tally per event. */
export interface ConventionRegCount {
  event: string;
  count: number;
}

/**
 * Provenance for the `newThisYear` KPI (issue #499). Mirrors the Membership
 * desk's `DeskKpiBasis` (`src/api/desk.types.ts`): `"observed"` means every
 * counted `joined` lifecycle event was recorded live off a real CE21
 * recompute; `"order_derived"` means at least one was reconstructed from
 * CE21 payment-history ordering (`apps.lifecycle.coverage_backfill`). `null`
 * only ever appears alongside `value: null`.
 */
export type NewMembersBasis = "observed" | "order_derived" | null;

/**
 * `GET /api/dashboard/summary` — new-members-this-year KPI (issue #499).
 *
 * Derived from `apps.lifecycle.models.LifecycleEvent` `joined` events — the
 * SAME evidence family the Membership desk's `new_members_ytd` KPI and the
 * lifecycle movement chart use — never from CE21's `current_start_date`
 * (which MAC re-stamps to Jan 1 on every calendar-year renewal; the prior bug,
 * #499, counted those renewals as new joins). `value` stays `null` — never a
 * fabricated or silently-undercounted number — until the lifecycle ledger
 * covers the whole year-to-date window.
 */
export interface NewMembersThisYear {
  value: number | null;
  basis: NewMembersBasis;
}

/** `GET /api/dashboard/summary` — financial roll-up (currency: USD cents). */
export interface FinancialSummary {
  /** Dues collected year-to-date, in whole dollars. */
  duesCollected: number;
  /** Outstanding / unpaid dues, in whole dollars. */
  outstanding: number;
  /**
   * Count of members in good standing — people whose CANONICAL derived standing
   * is `active`, i.e. exactly the `?status=active` member-list population and
   * the Membership desk's active buckets.
   *
   * Issue #592: this counted every non-deceased person (5,501 of 5,508 on the
   * live roster) because the status projection defaulted the blank CE21 status
   * column to "active". It sits beside `duesCollected` / `outstanding`, so it
   * read as a money-adjacent claim about the roster that was not true.
   */
  inGoodStanding: number;
  /**
   * Orders whose CE21 `payment_status` literal is outside the known enum, with
   * the count of orders carrying each (issue #600's fail-loud surface).
   *
   * Such an order is in NEITHER `duesCollected` NOR `outstanding` — deliberately,
   * because guessing which side it belongs on is how `lifetimeValue` came to be
   * $0 for everybody in the first place. The API has shipped this since #600 but
   * nothing on the frontend typed it, so the one signal saying "these two totals
   * do not cover every order" was invisible. Empty on a clean order book;
   * optional here so an older API degrades to no notice.
   */
  unknownPaymentStatuses?: UnknownPaymentStatus[];
}

/** One unrecognised `payment_status` literal + how many orders carry it. */
export interface UnknownPaymentStatus {
  status: string;
  orders: number;
}

/**
 * Full payload of `GET /api/dashboard/summary`.
 *
 * NB: no top-level `lapsed` count (R1, issue #421/#429). The canonical lapsed
 * figure now lives on the Membership operating desk (`GET /api/desk/summary`,
 * `src/api/desk.ts`), which derives it from the audience-standing projection —
 * the two disagreed in production (epic #421's "Pulse Lapsed 539 vs legacy 0"
 * symptom), so the duplicate was removed here rather than repointed.
 */
/**
 * Plain-English "how is this calculated" copy for a payload, keyed by the
 * figure's dotted path in that payload (issue #606). Owned by the backend
 * (`apps.dashboard.kpi_explain` / `apps.insights.kpi_explain`) exactly like the
 * Membership desk's `kpi_explanations` (#561) — the frontend renders it and
 * never writes it, so a sentence can't drift from the code that computed the
 * number. Optional on every consumer: an older backend (or a fixture that
 * omits it) degrades to no popover rather than to invented copy.
 */
export type MetricExplanations = Record<string, string>;

export interface DashboardSummary {
  /**
   * Every person record on the roster (issue #593/#606) — the denominator the
   * overview home's counts are slices of, so none of them renders as a naked
   * number. Optional: a payload without it degrades to the bare count.
   */
  totalPeople?: number;
  membersByTier: TierCount[];
  membersByDistrict: DistrictCount[];
  newThisYear: NewMembersThisYear;
  licenseExpiring: LicenseExpiringCount[];
  conventionRegs: ConventionRegCount[];
  financial: FinancialSummary;
  /** Backend-owned explain copy, keyed by payload path (#606). */
  explanations?: MetricExplanations;
}

/** One row of `GET /api/members` results (drill-down target; read-only). */
export interface MemberRow {
  id: number;
  name: string;
  tier: MemberTier;
  district: District;
  county: string;
  /**
   * Contact email, now on every row so the table can drill (#282). May be an
   * empty string when no email is on file (backend `allow_blank`).
   */
  email: string;
  /** ISO date (YYYY-MM-DD) the member's license expires, or null if none. */
  licenseExpiration: string | null;
  status: MemberStatus;
  /**
   * Lifetime value — sum of completed billable order charges (#282). `0` when
   * the member has no completed orders. Optional (#512): the API omits this
   * key entirely for callers who lack money visibility (e.g. the scoped
   * `reviewer` tenant role) — field-level redaction, not a data quality gap.
   */
  lifetimeValue?: number;
  /** Engagement score (0–100), or null for members not scored (e.g. deceased). */
  engagementScore: number | null;
  /** Lapse-risk band derived from the score, or null when not scored. */
  riskBand: RiskBand | null;
}

/** Full payload of `GET /api/members`. */
export interface MembersResponse {
  /** How many records match the filters in the request (paging is over this). */
  count: number;
  /**
   * How many records the roster holds with NO filter applied — the honest
   * denominator (#593). A client landing on `/members?category=doctor` saw a
   * naked "986" with nothing saying it was one slice of five; every count on
   * the Explorer now renders as "N of M" off the back of this. Optional so an
   * older API (or a fixture that predates it) simply falls back to showing the
   * filtered count alone.
   */
  totalUnfiltered?: number;
  results: MemberRow[];
}

/**
 * An account tag the org puts on a member record (E3-4). These are NOT mutually
 * exclusive — a record can be e.g. both a member and a PAC donor. `Deceased` is
 * surfaced on the detail view (suppressed from sync), so it lives here too.
 */
export type AccountTag =
  | "Member"
  | "Lead"
  | "PAC"
  | "MichiCare"
  | "Deceased";

/**
 * One entry in a member's tier/membership history (E3-4). Records when a member
 * moved to a tier; `to` is null while the entry is the current standing.
 */
export interface MembershipHistoryEntry {
  tier: MemberTier;
  /** ISO date (YYYY-MM-DD) the member entered this tier. */
  from: string;
  /** ISO date the member left this tier, or null if it is the current tier. */
  to: string | null;
}

/**
 * One row of a member's CE (continuing-education) transcript (E3-4). This is a
 * read-only PLACEHOLDER stub on the detail view — E9 fills the real transcript
 * surface later. The shape is intentionally minimal so E9 can extend it.
 */
export interface CeTranscriptEntry {
  id: number;
  /** Course / activity title. */
  course: string;
  /** ISO date (YYYY-MM-DD) the credit was earned. */
  completedOn: string;
  /** CE credit hours earned. */
  credits: number;
}

/**
 * `GET /api/members/:id` — the full per-member detail behind the read-only
 * profile (E3-4). Extends the list `MemberRow` shape with the fields the profile
 * surfaces: contact, membership history, license issuance, account tags, and a
 * CE-transcript stub (filled by E9).
 */
export interface MemberDetail extends MemberRow {
  email: string;
  phone: string;
  /** ISO date (YYYY-MM-DD) the license was issued, or null if none. */
  licenseIssued: string | null;
  /** License number / identifier, or null if none on file. */
  licenseNumber: string | null;
  /** Tier/membership history, most-recent first. */
  membershipHistory: MembershipHistoryEntry[];
  /** Account tags on this record (member/lead/PAC/MichiCare/Deceased). */
  tags: AccountTag[];
  /** Read-only CE-transcript stub (E9 fills the real surface). */
  ceTranscript: CeTranscriptEntry[];
  /**
   * Census-derived legislative districts (#341), or `null` when this person has
   * never been derived.
   *
   * `null` and a row with blank districts are DIFFERENT answers: `null` means
   * "we have not looked at this person yet", a row with
   * `confidence: "no_address"` means "we looked and there is no address to
   * look at". The person page says so differently.
   */
  legislativeDistricts: LegislativeDistricts | null;
  /** The Home and Office addresses from CE21's Accounts Report (the API gives one default address only), and the state licence list's (`stateList`), each with its own districts; null where there is none. */
  addresses: {
    home: PersonAddress | null;
    office: PersonAddress | null;
    stateList?: PersonAddress | null;
    /** True only when the latest state list that stored addresses does not have the person's licence at all; false when it is not known (no such load yet, no licence, or their row is held). */
    stateListAbsent?: boolean;
    /** Whether the person has a licence number: someone without one can never be on the state list, so nothing is said of it. */
    licensed?: boolean;
  };
}

/** Which address the legislative filters read: absent from the API's query = `on_file` (CE21's main address, the one on file), so old links keep their meaning. `either` is Home or Office. */
export type DistrictAddress = "home" | "office" | "either" | "on_file" | "state_list";
export const DISTRICT_ADDRESSES: readonly DistrictAddress[] = ["home", "office", "either", "on_file", "state_list"];

/** A Home or Office address as the Accounts Report gave it, or the state licence list's as the state published it. */
export interface PersonAddress {
  kind: "home" | "office" | "state_list";
  line1: string;
  /** "" when none. */
  line2: string;
  city: string;
  /** As in the file, usually the full name ("Michigan"). */
  state: string;
  /** Text, as in the file ("48933-1234", "N2L 3G1"). */
  postalCode: string;
  country: string;
  county: string;
  /** ISO day (Detroit) the address arrived. */
  importedAt: string | null;
  /** ISO day (Detroit) of the load that last confirmed it: the latest Accounts Report with the person in it, or the state list load its row came from. */
  loadedAt?: string | null;
  /** null = not matched yet ("Districts coming"). */
  districts: AddressDistricts | null;
}

/** The districts one address falls in: the CE21 block's shape, less its address, and `outside_us`. */
export interface AddressDistricts extends Omit<LegislativeDistricts, "address" | "confidence"> {
  confidence: LegislativeConfidence | "outside_us";
}

/** Legislative-district vocabulary, mirroring `apps.legislative.models`. */
export type LegislativeConfidence =
  | "exact"
  | "non_exact"
  | "unmatched"
  | "no_address"
  | "error";

/** Which service matched the address, mirroring `apps.legislative.models`. */
export type LegislativeMethod = "census_onelineaddress" | "votervoice" | "none";

/** A legislator sitting for a district now (VoterVoice, #341 phase 1b). */
export interface LegislativeOfficial {
  /** "Stephanie Chang (D)". */
  name: string;
  /** "Senator", "Representative". */
  title: string;
  email: string;
  phone: string;
}

/** The person page's legislative block (#341). */
export interface LegislativeDistricts {
  /** Two-digit state FIPS ("26" = Michigan); "" when nothing was derived. */
  stateFips: string;
  /** `<state FIPS>-<number>` key ("26-7"), or "" when unresolved. */
  usHouse: string;
  stateSenate: string;
  stateHouse: string;
  method: LegislativeMethod;
  /**
   * Who sits for each district now. Filled on a VoterVoice-matched row only:
   * a Census row's State Senate number is on the 2027 map, so naming that
   * district's sitting senator would name the wrong person.
   */
  officials: {
    usHouse: LegislativeOfficial[];
    stateSenate: LegislativeOfficial[];
    stateHouse: LegislativeOfficial[];
  };
  confidence: LegislativeConfidence;
  /** The member's current CE21 address on file, so staff can check the match by eye; "" for a part not on file. */
  address: { street: string; city: string; state: string; zip: string };
  /** ISO date the districts were last derived, or null. */
  derivedAt: string | null;
}

/**
 * License-expiry drill-down horizons, in days. A license-expiring summary card
 * links to `/members?expiringWithin=30` so the user lands on exactly the members
 * counted in that window. Mirrors the `licenseExpiring` summary buckets.
 */
export type ExpiringWindowDays = 30 | 60 | 90;

/**
 * Engagement drill-down cohorts (E14-1). `at-risk` is the medium+high union the
 * engagement card's headline links to; the individual bands drill to one band.
 */
export type RiskBandFilter = RiskBand | "at-risk";

/** Query parameters accepted by `GET /api/members`. */
/**
 * Renewal-pipeline buckets (#606) — mirrors `apps.dashboard.contract.
 * RENEWAL_BUCKETS` / `apps.insights.selectors.RENEWAL_BUCKETS`. An
 * unrecognised value is a 400 at the API (never a silently empty page), so the
 * URL parser drops anything outside this union rather than forwarding it.
 */
export type RenewalBucket = "overdue" | "due30" | "due60" | "due90";

/** An event's groups, as `?eventGroup=` takes them (#705, `apps.dashboard.contract.EVENT_GROUPS`). */
export type EventGroup = "everyone" | "members" | "non_members" | "vendors" | "came_last_time";

export interface MembersQuery {
  tier?: MemberTier;
  district?: District;
  status?: MemberStatus;
  /** Free-text search over name / county. */
  q?: string;
  /**
   * Restrict to members whose license expires within this many days of "now"
   * (inclusive, future-only). The drill-down target for license-expiry cards.
   */
  expiringWithin?: ExpiringWindowDays;
  /** Restrict to an engagement risk band (E14-1) — the engagement drill-down. */
  riskBand?: RiskBandFilter;
  // --- Insights drill params (#282) — mirror the facet cross-filter logic so a
  //     click from Composition / the District map / Pulse truly filters the table,
  //     not just the facet rail. These are the same dimensions getFacets accepts.
  /** Super-category key (insights `category`), e.g. "ca" | "spouse". */
  category?: string;
  /** No-email sub-segment narrowing (`active|lapsed|lead`). */
  segment?: string;
  /** Data-health "missing field" worklist (`license|county`). */
  missing?: "license" | "county";
  /** Tri-state has-email filter (the no-email worklist). */
  hasEmail?: boolean;
  /** County name (composition county facet). */
  county?: string;
  /** Engagement band (insights vocabulary `high|medium|low|unscored`). */
  engagementBand?: EngagementBand;
  // --- Association Pulse worklist drill-downs (#606). The dashboard emitted
  //     both of these as links long before the API understood them, so the
  //     click landed on the unfiltered roster. Now real filters, resolved
  //     server-side through the same predicates the Pulse counts with.
  /** Renewal-pipeline bucket — the "Renewals overdue / due in N days" rows. */
  renewal?: RenewalBucket;
  /** Lapsed within this many days — the recently-lapsed win-back row. */
  lapsedWithin?: number;
  // --- legislative districts (#341). `<state FIPS>-<number>` keys ("26-7"); a
  //     bare number is read as the home state by the backend. NOT `district`
  //     above (MAC's county-based Districts 1–8) — a different concept.
  /** US congressional district key. */
  usHouse?: string;
  /** State Senate district key. */
  stateSenate?: string;
  /** State House district key. */
  stateHouse?: string;
  /** Which address the three above read (absent = the CE21 address on file). */
  districtAddress?: DistrictAddress;
  /** An event's people (#705): its ref (`ce21:<product id>`) and, optionally, one of its groups. */
  event?: string;
  eventGroup?: EventGroup;
  /** People → Licences (#635): the people behind one of its four counts. */
  licenseState?: LicenseState;
  /** Licence reminders (#339): a step's people due, or held back for one reason (`two_months:opted_out`). */
  licenceReminder?: string;
  /** Who an email reaches (#716): `yes`, or the people one reason leaves out (`opted_out`). */
  reachable?: string;
  limit?: number;
  offset?: number;
}

/** One integration's sync run, from `GET /api/sync/health`. */
export interface SyncRun {
  integration: string;
  /** ISO datetime of the last run. */
  lastRun: string;
  created: number;
  updated: number;
  skippedOptOut: number;
  suppressedDeceased: number;
  errors: number;
}

/** Full payload of `GET /api/sync/health`. */
export interface SyncHealth {
  runs: SyncRun[];
}

/** `GET /api/dashboard/engagement` — one risk-band bucket: band → count. */
export interface EngagementBandCount {
  band: RiskBand;
  count: number;
}

/** One weighted signal feeding the engagement score (for explainability). */
export interface EngagementSignal {
  key: string;
  label: string;
  weight: number;
  description: string;
}

/** The score thresholds that define the bands. */
export interface EngagementThresholds {
  lowRiskMin: number;
  mediumRiskMin: number;
}

/**
 * Full payload of `GET /api/dashboard/engagement` (E14-1) — the member
 * engagement / lapse-risk distribution the Command Center card renders.
 */
export interface EngagementSummary {
  /**
   * The scored universe: active/lapsed-in-grace benefit-holders only (#498).
   * Deceased members AND non-members (leads/orgs/prospects — anyone who has
   * never held a paid tier membership) are excluded entirely, not counted
   * here and not represented anywhere in `bands` — this is what the card's
   * "of N active members" copy reports against.
   */
  total: number;
  /** Mean engagement score across scored members (0–100). */
  averageScore: number;
  /** Count per risk band (low / medium / high). */
  bands: EngagementBandCount[];
  /** Members needing re-engagement (medium + high). */
  atRisk: number;
  /** Members in the high-risk band (most urgent). */
  highRisk: number;
  /** The weighted signals feeding the score (documentation / tooltip). */
  signals: EngagementSignal[];
  /** Score thresholds defining the bands. */
  thresholds: EngagementThresholds;
  /** Backend-owned explain copy, keyed by payload path (#606). */
  explanations?: MetricExplanations;
}
